import React, { useState, useEffect, useCallback } from 'react';
import { useMap } from '../map/MapContext';

type CoordFormat = 'DD' | 'DMS' | 'DDM' | 'UTM' | 'MGRS';
const FORMATS: CoordFormat[] = ['DD', 'DMS', 'DDM', 'UTM', 'MGRS'];

function formatDD(lat: number, lng: number): string {
  return `${lat.toFixed(5)}°, ${lng.toFixed(5)}°`;
}

function formatDMS(lat: number, lng: number): string {
  const toDMS = (v: number, pos: string, neg: string) => {
    const dir = v >= 0 ? pos : neg;
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const m = Math.floor((abs - d) * 60);
    const s = ((abs - d - m / 60) * 3600).toFixed(1);
    return `${d}°${String(m).padStart(2, '0')}'${String(s).padStart(4, '0')}"${dir}`;
  };
  return `${toDMS(lat, 'N', 'S')} ${toDMS(lng, 'E', 'W')}`;
}

function formatDDM(lat: number, lng: number): string {
  const toDDM = (v: number, pos: string, neg: string) => {
    const dir = v >= 0 ? pos : neg;
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const m = ((abs - d) * 60).toFixed(3);
    return `${d}°${m}'${dir}`;
  };
  return `${toDDM(lat, 'N', 'S')} ${toDDM(lng, 'E', 'W')}`;
}

function formatCoord(lat: number, lng: number, fmt: CoordFormat): string {
  switch (fmt) {
    case 'DD': return formatDD(lat, lng);
    case 'DMS': return formatDMS(lat, lng);
    case 'DDM': return formatDDM(lat, lng);
    case 'UTM': return `UTM ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    case 'MGRS': return `MGRS ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    default: return formatDD(lat, lng);
  }
}

export const StatusBar: React.FC = () => {
  const { map } = useMap();
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(0);
  const [format, setFormat] = useState<CoordFormat>('DD');

  useEffect(() => {
    if (!map) return;
    const onMove = () => {
      const c = map.getCenter();
      setCenter({ lat: c.lat, lng: c.lng });
      setZoom(map.getZoom());
    };
    map.on('move', onMove);
    onMove();
    return () => { map.off('move', onMove); };
  }, [map]);

  const cycleFormat = useCallback(() => {
    setFormat((prev) => {
      const idx = FORMATS.indexOf(prev);
      return FORMATS[(idx + 1) % FORMATS.length];
    });
  }, []);

  return (
    <div className="statusbar">
      <div className="statusbar-left">
        <span className="statusbar-item" style={{ fontWeight: 600, color: 'var(--accent-light)' }}>
          ✈ EFB Web
        </span>
      </div>
      <div className="statusbar-center">
        <button
          className="statusbar-item"
          onClick={cycleFormat}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}
          title={`Click to cycle format (${format})`}
        >
          📍 {formatCoord(center.lat, center.lng, format)}
        </button>
        <span className="statusbar-item">🔍 Z{zoom.toFixed(1)}</span>
      </div>
      <div className="statusbar-right">
        <span className="statusbar-item status-green">● Online</span>
      </div>
    </div>
  );
};
